import React, { useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { databasePoints, databaseSeriesColor, formatDatabaseNumber } from '../../Utils/database_history_utils';
import historyTooltip from './HistoryTooltip';

export default React.memo(function DatabaseChart({
  records,
  field,
  xAxis,
  yAxis,
  groupBy,
  zoneLabel,
  fieldLabel
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const points = useMemo(
    () => databasePoints(records, { field, xAxis, yAxis, groupBy }),
    [records, field, xAxis, yAxis, groupBy]
  );
  const commandLabel = (command) => t(`database_command_${command}`, { defaultValue: command });
  const shortCommandLabel = (command) => t(`database_short_${command}`, { defaultValue: commandLabel(command) });
  const groups = [...new Set(points.map((point) => point.group))].sort();
  const data = {
    datasets: groups.map((group) => ({
      label:
        groupBy === 'zone'
          ? zoneLabel(group)
          : groupBy === 'command'
            ? shortCommandLabel(group)
            : t('database_points'),
      data: points.filter((point) => point.group === group),
      backgroundColor: groupBy === 'none' ? theme.palette.primary.main : databaseSeriesColor(group),
      pointRadius: 4,
      pointHoverRadius: 6,
      pointHitRadius: 8
    }))
  };
  const scale = (axis) => {
    const time = axis === 'received' || axis === 'source';
    const categorical = axis === 'zone' || axis === 'command';
    const labels = categorical
      ? [
          ...new Set(
            points.map((point) =>
              axis === 'zone' ? point.record.zone : point.record.row.payload.command
            )
          )
        ].sort()
      : undefined;
    return {
      type: categorical ? 'category' : 'linear',
      ...(labels ? { labels } : {}),
      title: {
        display: true,
        text: axis === 'value' ? fieldLabel(field) : t(`database_axis_${axis}`),
        color: theme.palette.text.primary
      },
      grid: { color: theme.palette.divider },
      ticks: {
        color: theme.palette.text.secondary,
        maxTicksLimit: time ? 4 : 8,
        maxRotation: 0,
        callback: function (value) {
          if (time) return [new Date(value).toLocaleDateString(), new Date(value).toLocaleTimeString()];
          if (axis === 'zone') return zoneLabel(this.getLabelForValue(value));
          if (axis === 'command') return shortCommandLabel(this.getLabelForValue(value));
          return String(Number(formatDatabaseNumber(value)));
        }
      }
    };
  };
  if (!points.length) return <p>{t('database_no_points')}</p>;
  return (
    <div style={{ height: 340, position: 'relative', minWidth: 0 }}>
      <Scatter
        plugins={[historyTooltip]}
        data={data}
        aria-label={t('database_chart')}
        role="img"
        options={{
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          font: { family: theme.typography.fontFamily },
          scales: { x: scale(xAxis), y: scale(yAxis) },
          plugins: {
            legend: {
              display: groupBy !== 'none',
              labels: { color: theme.palette.text.primary, usePointStyle: true }
            },
            tooltip: {
              intersect: true,
              callbacks: {
                title: (items) => new Date(items[0].raw.record.row.received_at).toLocaleString(),
                label: ({ raw }) => [
                  commandLabel(raw.record.row.payload.command),
                  zoneLabel(raw.record.zone),
                  ...(field ? [`${fieldLabel(field)}: ${typeof raw.record.fields[field] === 'number' ? formatDatabaseNumber(raw.record.fields[field]) : String(raw.record.fields[field])}`] : [])
                ]
              }
            }
          }
        }}
      />
    </div>
  );
});
